import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { wishlistApi } from '@/api/service';
import type { WishlistItem } from '@/types';

interface WishlistState {
  items: WishlistItem[];
  isLoading: boolean;
  error: string | null;
  itemCount: number;

  fetchWishlist: () => Promise<void>;
  addToWishlist: (item: WishlistItem) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  clearWishlist: () => void;
  isInWishlist: (productId: string) => boolean;
}

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,
      error: null,
      itemCount: 0,

      fetchWishlist: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await wishlistApi.getWishlist();
          if (response.success && response.data) {
            const items: WishlistItem[] = response.data.items.map(item => item.product);
            set({ items, itemCount: items.length });
          } else {
            set({ error: response.error?.message || 'Failed to fetch wishlist' });
          }
        } catch {
          set({ error: 'Network error' });
        } finally {
          set({ isLoading: false });
        }
      },

      addToWishlist: async (item: WishlistItem) => {
        const { items } = get();
        if (items.some(i => i.id === item.id)) return;

        const newItems = [...items, item];
        set({ items: newItems, itemCount: newItems.length, error: null });

        try {
          const response = await wishlistApi.addToWishlist(item.id);
          if (!response.success) {
            set({ items, itemCount: items.length, error: 'Failed to add to wishlist' });
          }
        } catch {
          set({ items, itemCount: items.length, error: 'Sync failed' });
        }
      },

      removeFromWishlist: async (productId: string) => {
        const { items } = get();
        const newItems = items.filter(i => i.id !== productId);
        set({ items: newItems, itemCount: newItems.length, error: null });

        try {
          const response = await wishlistApi.removeFromWishlist(productId);
          if (!response.success) {
            set({ items, itemCount: items.length, error: 'Failed to remove from wishlist' });
          }
        } catch {
          set({ items, itemCount: items.length, error: 'Sync failed' });
        }
      },

      clearWishlist: () => {
        set({ items: [], itemCount: 0, error: null });
      },

      isInWishlist: (productId: string) => get().items.some(i => i.id === productId),
    }),
    {
      name: 'bst_wishlist_storage',
      storage: createJSONStorage(() => window.localStorage),
    }
  )
);
